import React, { useState } from 'react';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Square, 
  ShieldCheck, 
  ShieldX, 
  UserCheck,
  UserMinus,
  Eye,
  Crown,
  UserCog,
  Trash2
} from 'lucide-react';
import UserDetailsPopup from './UserDetailsPopup';

function UserCard({ user, selectedUsers, setSelectedUsers, onUserAction, theme, currentThemeName }) {
  const [showUserDetails, setShowUserDetails] = useState(false);
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { 
        color: '#10b981', 
        bg: 'rgba(16, 185, 129, 0.1)', 
        icon: <CheckCircle2 size={14} />,
        text: 'Active'
      },
      blocked: { 
        color: '#ef4444', 
        bg: 'rgba(239, 68, 68, 0.1)', 
        icon: <XCircle size={14} />,
        text: 'Blocked'
      },
      pending: { 
        color: '#f59e0b', 
        bg: 'rgba(245, 158, 11, 0.1)', 
        icon: <Clock size={14} />,
        text: 'Pending'
      },
      stopped: { 
        color: '#6b7280', 
        bg: 'rgba(107, 114, 128, 0.1)', 
        icon: <Square size={14} />,
        text: 'Stopped'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 20,
        color: config.color,
        backgroundColor: config.bg,
        border: `1px solid ${config.color}20`,
        fontSize: 12,
        fontWeight: 600
      }}>
        <span>{config.icon}</span>
        <span>{config.text}</span>
      </span>
    );
  };

  return (
    <div style={{
      background: currentThemeName === 'dark'
        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)'
        : currentThemeName === 'beige'
        ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.8) 0%, rgba(250, 235, 215, 0.9) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)',
      border: currentThemeName === 'dark'
        ? '1px solid rgba(59, 130, 246, 0.15)'
        : currentThemeName === 'beige'
        ? '1px solid rgba(139, 69, 19, 0.1)'
        : '1px solid rgba(59, 130, 246, 0.08)',
      borderRadius: 16,
      padding: 20,
      marginBottom: 16,
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(8px)',
      position: 'relative',
      overflow: 'hidden'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
      e.currentTarget.style.boxShadow = currentThemeName === 'dark'
        ? '0 12px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)'
        : currentThemeName === 'beige'
        ? '0 12px 32px rgba(139, 69, 19, 0.12), 0 0 0 1px rgba(139, 69, 19, 0.08)'
        : '0 12px 32px rgba(59, 130, 246, 0.1), 0 0 0 1px rgba(59, 130, 246, 0.06)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      {/* Background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: currentThemeName === 'dark'
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(147, 51, 234, 0.02) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.02) 0%, rgba(160, 82, 45, 0.01) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.01) 0%, rgba(147, 51, 234, 0.005) 100%)',
        pointerEvents: 'none'
      }} />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <input
            type="checkbox"
            checked={selectedUsers.includes(user.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedUsers([...selectedUsers, user.id]);
              } else {
                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
              }
            }}
            style={{
              width: 20,
              height: 20,
              accentColor: currentThemeName === 'dark' ? '#10b981' : '#10b981',
              cursor: 'pointer',
              borderRadius: '4px',
              border: currentThemeName === 'dark' 
                ? '2px solid rgba(16, 185, 129, 0.3)' 
                : '2px solid rgba(16, 185, 129, 0.2)',
              backgroundColor: currentThemeName === 'dark' 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(16, 185, 129, 0.05)',
              transition: 'all 0.2s ease'
            }}
          />
          <div>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              {user.first_name} {user.last_name}
              {(user.is_admin || user.isAdmin) && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: 'rgba(245, 158, 11, 0.15)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.35)'
                }}>
                  Admin
                </span>
              )}
            </div>
            <div style={{
              fontSize: 14,
              color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary
            }}>
              {user.email}
            </div>
          </div>
        </div>
        <div>
          {getStatusBadge(user.status || 'pending')}
        </div>
      </div>
      
      <div style={{
        marginBottom: 16,
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{
            fontSize: 13,
            color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary,
            fontWeight: 500
          }}>
            Joined:
          </span>
          <span style={{
            fontSize: 13,
            color: currentThemeName === 'dark' ? '#e5e7eb' : theme.text,
            fontWeight: 600
          }}>
            {new Date(user.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        position: 'relative',
        zIndex: 1
      }}>
        {/* View Details Button */}
        <button
          onClick={() => setShowUserDetails(true)}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
          }}
        >
          <Eye size={14} />
          <span>View Details</span>
        </button>

        {user.status === 'pending' && (
          <button
            onClick={() => onUserAction(user.id, 'approve')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Approve User"
          >
            <UserCheck size={14} />
            <span>Approve</span>
          </button>
        )}
        {user.status !== 'active' && (
          <button
            onClick={() => onUserAction(user.id, 'activate')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Activate User"
          >
            <ShieldCheck size={14} />
            <span>Activate</span>
          </button>
        )}
        {user.status !== 'blocked' && (
          <button
            onClick={() => onUserAction(user.id, 'block')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Block User"
          >
            <ShieldX size={14} />
            <span>Block</span>
          </button>
        )}
        {user.status !== 'stopped' && (
          <button
            onClick={() => onUserAction(user.id, 'stop')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Stop User"
          >
            <UserMinus size={14} />
            <span>Stop</span>
          </button>
        )}

        {/* Admin status buttons */}
        {user.is_admin || user.isAdmin ? (
          <button
            onClick={() => onUserAction(user.id, 'removeAdmin')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Remove Admin Privileges"
          >
            <UserCog size={14} />
            <span>Remove Admin</span>
          </button>
        ) : (
          <button
            onClick={() => onUserAction(user.id, 'setAdmin')}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
            title="Grant Admin Privileges"
          >
            <Crown size={14} />
            <span>Set As Admin</span>
          </button>
        )}

        {/* Delete button */}
        <button
          onClick={() => onUserAction(user.id, 'delete')}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
          title="Delete User"
        >
          <Trash2 size={14} />
          <span>Delete</span>
        </button>
        
      </div>

      {/* User Details Popup */}
      <UserDetailsPopup
        user={user}
        isOpen={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        theme={theme}
        currentThemeName={currentThemeName}
      />
    </div>
  );
}

export default UserCard; 